import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CollapsibleBoxComponent from '../../../components/CollapsibleBox/CollapsibleBoxComponent';
import TextFieldComponent from '../../../components/TextField/TextFieldComponent';
import DropdownComponent from '../../../components/DropDown/DropDownComponent';
import LoadingDots from '../../../components/LoadingDots/LoadingDots';
import { withMinDuration } from '../../../utils/withMinDuration';
import petTemplate from '../../../json/components/subscreens/PetDetailsTemplate.json';
import useValidationRegistry from '../useValidationRegistry';

const componentMap = {
  'text-field': TextFieldComponent,
  dropdown: DropdownComponent,
};

const applyValuesToTemplate = (template, values) => {
  const merged = JSON.parse(JSON.stringify(template));

  Object.entries(values || {}).forEach(([key, value]) => {
    if (merged[key]) {
      merged[key].value = value ?? '';
    }
  });

  return merged;
};

const formatDateForDisplay = (rawDate) => {
  if (!rawDate) return '';

  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return String(rawDate || '');

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const mapPetToTemplate = (pet = {}) => ({
  petName: pet?.PetName || pet?.name || '',
  colour: pet?.PetColour || pet?.Colour || pet?.colour || '',
  breed: pet?.PetBreed || pet?.Breed || pet?.breed || '',
  dob: formatDateForDisplay(pet?.DOB || pet?.dob),
  petGender: pet?.PetGender || pet?.Gender || pet?.gender || '',
  petType: pet?.PetType || pet?.Type || pet?.type || '',
});

const getFreshPet = () => applyValuesToTemplate(petTemplate, {});

// Pre-live: pet management (add / edit / reset) is temporarily disabled so the
// account page is view-only. Flip to true to restore these actions post-live.
const ALLOW_PET_EDITING = false;

const PetDetailsContent = ({ isMobile, savedPetsData = [], onResetPets }) => {
  const { registerValidation, validateAll } = useValidationRegistry();
  const [pets, setPets] = useState([]);
  const [showNewPet, setShowNewPet] = useState(false);
  const [newPet, setNewPet] = useState(getFreshPet());
  const [isResettingEmptyPets, setIsResettingEmptyPets] = useState(false);
  const [resettingPetIndices, setResettingPetIndices] = useState(
    () => new Set()
  );

  useEffect(() => {
    setPets(
      Array.isArray(savedPetsData)
        ? savedPetsData.map((pet) =>
            applyValuesToTemplate(petTemplate, mapPetToTemplate(pet))
          )
        : []
    );
    setShowNewPet(false);
    setNewPet(getFreshPet());
  }, [savedPetsData]);

  const handleFieldChange = (petIndex, fieldName, value) => {
    setPets((prev) => {
      const next = [...prev];
      if (!next[petIndex] || !next[petIndex][fieldName]) {
        return prev;
      }

      next[petIndex] = {
        ...next[petIndex],
        [fieldName]: {
          ...next[petIndex][fieldName],
          value,
        },
      };

      return next;
    });
  };

  const handleNewPetFieldChange = (fieldName, value) => {
    setNewPet((prev) => {
      if (!prev[fieldName]) return prev;

      return {
        ...prev,
        [fieldName]: {
          ...prev[fieldName],
          value,
        },
      };
    });
  };

  const addNewPetToList = () => {
    if (!validateAll()) {
      return;
    }

    setPets((prev) => [...prev, newPet]);
    setNewPet(getFreshPet());
    setShowNewPet(false);
  };

  const resetEmptyPets = async () => {
    setIsResettingEmptyPets(true);
    try {
      await withMinDuration(
        onResetPets?.(undefined, {
          showLoader: false,
          forceRefresh: true,
        })
      );
    } catch (error) {
      // ignore
    } finally {
      setIsResettingEmptyPets(false);
    }
  };

  const resetSavedPet = async (index) => {
    setResettingPetIndices((prev) => new Set(prev).add(index));
    try {
      await withMinDuration(
        onResetPets?.(undefined, {
          showLoader: false,
          forceRefresh: true,
        })
      );
    } catch (error) {
      // ignore
    } finally {
      setResettingPetIndices((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }
  };

  const renderPetFields = (
    pet,
    onChange,
    registerFieldValidation = null,
    isReadOnly = false
  ) => {
    const entries = Object.entries(pet).filter(
      ([, config]) => config && typeof config === 'object'
    );

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 416px)',
          columnGap: isMobile ? 0 : '32px',
          rowGap: isMobile ? 4 : '16px',
          justifyContent: 'center',
          alignItems: 'start',
        }}
      >
        {entries.map(([name, question]) => {
          const Component = componentMap[question.type];
          if (!Component) return null;
          const isFullWidth = question.customSize === 'accounts-full';

          return (
            <Box
              key={name}
              sx={{
                width: '100%',
                gridColumn: !isMobile && isFullWidth ? '1 / -1' : 'auto',
              }}
            >
              <Component
                name={name}
                question={question}
                value={question.value}
                onChange={(fieldName, value) => onChange(fieldName, value)}
                registerValidation={registerFieldValidation}
                isMobile={isMobile}
                layoutVariant="account"
                readOnly={isReadOnly}
              />
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderNewPetCard = () => (
    <CollapsibleBoxComponent
      title="New pet"
      isMobile={isMobile}
      defaultExpanded
    >
      <Box sx={{ px: isMobile ? 1.5 : 2 }}>
        {renderPetFields(newPet, handleNewPetFieldChange, registerValidation)}

        <Typography
          sx={{
            mt: 2,
            fontFamily: 'Inter, sans-serif',
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            color: '#738289',
            textAlign: 'center',
          }}
        >
          Missing API, so this will not load a new pet
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mt: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch',
          }}
        >
          <Button
            color="inherit"
            onClick={() => {
              setNewPet(getFreshPet());
              setShowNewPet(false);
            }}
            sx={{
              backgroundColor: '#E2E5EA',
              px: 4,
              py: 1.5,
              borderRadius: '20px',
              fontWeight: 600,
              lineHeight: '24px',
              fontFamily: 'Inter, sans-serif',
              fontSize: isMobile ? '0.8125rem' : '1.25rem',
              color: '#1C2F3A',
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={addNewPetToList}
            sx={{
              backgroundColor: '#C7CA00',
              px: 4,
              py: 1.5,
              borderRadius: '20px',
              fontWeight: 600,
              lineHeight: '24px',
              fontFamily: 'Inter, sans-serif',
              fontSize: isMobile ? '0.8125rem' : '1.25rem',
              color: '#1C2F3A',
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Add pet +
          </Button>
        </Box>
      </Box>
    </CollapsibleBoxComponent>
  );

  if (!pets.length) {
    return (
      <Box>
        <CollapsibleBoxComponent title="Pets" isMobile={isMobile}>
          <Box sx={{ p: isMobile ? 1.5 : 2 }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: isMobile ? '0.875rem' : '1rem',
                color: '#1C2F3A',
              }}
            >
              No pets found on this profile.
            </Typography>
            {ALLOW_PET_EDITING && onResetPets ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'stretch',
                }}
              >
                <Button
                  onClick={resetEmptyPets}
                  disabled={isResettingEmptyPets}
                  sx={{
                    backgroundColor: '#E2E5EA',
                    px: 4,
                    py: 1.5,
                    borderRadius: '20px',
                    fontWeight: 600,
                    fontFamily: 'Inter, sans-serif',
                    fontSize: isMobile ? '0.8125rem' : '1.25rem',
                    color: '#1C2F3A',
                    textTransform: 'none',
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { sm: '120px' },
                  }}
                >
                  {isResettingEmptyPets ? <LoadingDots /> : 'Reset'}
                </Button>
              </Box>
            ) : null}
            {ALLOW_PET_EDITING && !showNewPet ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'stretch',
                }}
              >
                <Button
                  onClick={() => setShowNewPet(true)}
                  sx={{
                    height: '40px',
                    backgroundColor: '#FF0074',
                    borderRadius: '16px',
                    px: 2,
                    py: 1,
                    gap: '12px',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    fontSize: '1rem',
                    lineHeight: '1rem',
                    letterSpacing: '-0.002em',
                    fontFamily: 'BInter, Inter, sans-serif',
                    textTransform: 'none',
                    boxShadow: 'none',
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      backgroundColor: '#e10068',
                    },
                  }}
                >
                  Add pet +
                </Button>
              </Box>
            ) : null}
          </Box>
        </CollapsibleBoxComponent>

        {ALLOW_PET_EDITING && showNewPet ? renderNewPetCard() : null}
      </Box>
    );
  }

  return (
    <Box>
      {pets.map((pet, index) => (
        <CollapsibleBoxComponent
          key={index}
          title={pet?.petName?.value || pet?.PetName || `Pet ${index + 1}`}
          isMobile={isMobile}
          defaultExpanded={index === 0}
        >
          <Box sx={{ px: isMobile ? 1.5 : 2 }}>
            {renderPetFields(
              pet,
              (fieldName, value) => handleFieldChange(index, fieldName, value),
              null,
              true
            )}

            {ALLOW_PET_EDITING && onResetPets ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                  mt: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'stretch',
                }}
              >
                <Button
                  color="inherit"
                  onClick={() => resetSavedPet(index)}
                  disabled={resettingPetIndices.has(index)}
                  sx={{
                    backgroundColor: '#E2E5EA',
                    px: 4,
                    py: 1.5,
                    borderRadius: '20px',
                    fontWeight: 600,
                    lineHeight: '24px',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: isMobile ? '0.8125rem' : '1.25rem',
                    color: '#1C2F3A',
                    textTransform: 'none',
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { sm: '120px' },
                  }}
                >
                  {resettingPetIndices.has(index) ? <LoadingDots /> : 'Reset'}
                </Button>
              </Box>
            ) : null}
          </Box>
        </CollapsibleBoxComponent>
      ))}

      {ALLOW_PET_EDITING && !showNewPet ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch',
          }}
        >
          <Button
            onClick={() => setShowNewPet(true)}
            sx={{
              height: '40px',
              backgroundColor: '#FF0074',
              borderRadius: '16px',
              px: 2,
              py: 1,
              gap: '12px',
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: '1rem',
              lineHeight: '1rem',
              letterSpacing: '-0.002em',
              fontFamily: 'BInter, Inter, sans-serif',
              textTransform: 'none',
              boxShadow: 'none',
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                backgroundColor: '#e10068',
              },
            }}
          >
            Add pet +
          </Button>
        </Box>
      ) : null}

      {ALLOW_PET_EDITING && showNewPet ? renderNewPetCard() : null}
    </Box>
  );
};

export default PetDetailsContent;
